// "Removed · Undo" for things that can't come back once the room has them (a deleted file, suppressed previews).
// Nothing is sent until the toast runs out: until then the thing is only hidden on this device, so Undo is instant
// and needs no server support. The timers live here, not in a component, so scrolling the message away or switching
// channels doesn't lose a removal; leaving the page sends whatever is still waiting. Once sent, the thing stays hidden
// for a few seconds more: the room's answer is what takes it out of the message (`Room.mediaEdit`, #273), and it comes a
// round trip later. A refusal takes nothing out, so the thing shows again when the wait is over.
import { useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import "./media.css";

const UNDO_MS = 5000;
/** How long a removal that was sent stays hidden, for the room to answer. */
const ANSWER_MS = 3000;

interface Pending {
  id: string;
  label: string;
  commit: () => void;
  timer: ReturnType<typeof setTimeout>;
}

let pending: Pending[] = [];
/** Ids sent and waiting for the room's answer: hidden, with no toast. */
const sent = new Set<string>();
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => void listeners.delete(l);
};

function settle(id: string, run: boolean) {
  const p = pending.find((x) => x.id === id);
  if (!p) return;
  clearTimeout(p.timer);
  pending = pending.filter((x) => x !== p);
  if (run) {
    // Hold it hidden before telling the page, so it never flashes back while the room answers.
    sent.add(id);
    setTimeout(() => {
      sent.delete(id);
      changed();
    }, ANSWER_MS);
    p.commit();
  } else {
    sent.delete(id);
  }
  changed();
}

/** Hides `id` now and runs `commit` in a few seconds unless the person presses Undo. */
export function removeWithUndo(id: string, label: string, commit: () => void) {
  if (pending.some((p) => p.id === id)) return;
  ensureHost();
  pending = [...pending, { id, label, commit, timer: setTimeout(() => settle(id, true), UNDO_MS) }];
  changed();
}

export const undo = (id: string) => settle(id, false);

/** True while `id` is waiting to be removed, or was sent and the room has not answered yet (draw it as gone). */
export function usePendingRemoval(id: string): boolean {
  return useSyncExternalStore(subscribe, () => sent.has(id) || pending.some((p) => p.id === id));
}

// Leaving or reloading the page: send what's waiting now (the socket is still open during pagehide).
addEventListener("pagehide", () => {
  for (const p of [...pending]) settle(p.id, true);
});

function Toasts() {
  const list = useSyncExternalStore(subscribe, () => pending);
  if (!list.length) return null;
  return createPortal(
    <div className="undo-toasts" role="status" aria-live="polite">
      {list.map((p) => (
        <div key={p.id} className="undo-toast">
          <span>{p.label}</span>
          <button type="button" onClick={() => undo(p.id)}>Undo</button>
          <span className="undo-timer" style={{ animationDuration: `${UNDO_MS}ms` }} aria-hidden="true" />
        </div>
      ))}
    </div>,
    document.body,
  );
}

// The toasts get their own little React root, so any component can ask for one without the page wiring it up.
let hosted = false;
function ensureHost() {
  if (hosted) return;
  hosted = true;
  const el = document.createElement("div");
  document.body.appendChild(el);
  createRoot(el).render(<Toasts />);
}
